import React, { useState, useEffect, useCallback } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { Spinner, Row } from "reactstrap"
import philGEPSLogo from "assets/images/philgeps-logo.png"
import roleTypes from "constants/role"
//REDUX
import { useSelector, useDispatch, persistor } from "storeRtk"
import { setUserDetails } from "slices/AuthUser"
import {
  saveTokenThunk,
  getMgepsTokenThunk,
  resetAuthStateThunk,
  saveAuthType,
} from "slices/authentication.js"

const Authenticate = () => {
  //meta title
  document.title = "Authentication | PhilGEPS - WEB"

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { token, userData, logs } = useSelector(state => state.authReduxSlice)
  const { error, message, loading, success, type, status } = logs
  const [authenticationProgress, setAuthenticationProgress] =
    useState("Authenticating...")
  const [counter, setCounter] = useState(0)
  const [isUrlToken, setIsUrlToken] = useState(false)

  const handleAuthRedirect = user => {
    console.log("AutoLogin Page (auth)", user)
    if (!user) {
      setTimeout(() => {
        setAuthenticationProgress(
          "Authentication is successful but User data is empty, please relogin."
        )
      }, 1000) // 1 sec
      setTimeout(() => {
        setAuthenticationProgress(
          "Login Failed. Redirecting to PhilGEPS login..."
        )
        window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
        // window.location.href = process.env.REACT_APP_PHILGEPS_DEV_URL
      }, 1000) // 1 sec
    }
    if (user?.role_type === roleTypes["ADMIN"]) navigate("/admin/dashboard")
    if (user?.role_type === roleTypes["AGENCY"]) navigate("/agency/home")
    if (user?.role_type === roleTypes["MERCHANT"])
      navigate("/merchant/dashboard")
    localStorage.setItem("authUser", JSON.stringify(user))
    dispatch(setUserDetails(user))
  }

  const getTokenInUrl = () => {
    //PHILGEPS TOKEN
    const urlToken = searchParams.get("token") // get("<urlParamName>")
    // console.log("INITIALIZED getTokenInURL: ", urlToken)
    setIsUrlToken(true)
    if (urlToken) {
      dispatch(saveTokenThunk(urlToken))
      if (urlToken?.includes("|")) {
        dispatch(saveAuthType("philgeps-auth-svc"))
      }
    } else {
      // console.log("No/Invalid token detected, redirecting...")
      if (token?.length === 0) {
        setAuthenticationProgress("No token detected.. ")
      }
      setTimeout(() => {
        setAuthenticationProgress(
          "Login Failed. Redirecting to PhilGEPS login..."
        )
        window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
        // window.location.href = process.env.REACT_APP_PHILGEPS_DEV_URL
      }, 1000) // 1 sec
    }
  }

  useState(() => {
    //   // Refresh synchronously #1 ----------------------------------------
    // console.log("TRIGGERED RESET AUTH STATE")
    dispatch(resetAuthStateThunk())
  })

  useEffect(() => {
    // Get the Token in URL #2 ----------------------------------------
    // console.log("Getting Token in URL...", token)
    getTokenInUrl()
  }, [])

  useEffect(() => {
    console.log("LOGS: ", logs)
    if (success) {
      // console.log("TRIGGERED getMgepsToken SUCCESS: ", userData)
      setAuthenticationProgress("Successfully authenticated ✅")
      handleAuthRedirect(userData)
    } else if ((error && status === 400) || status === 404) {
      // REDIRECT TO PHILGEPS LOGIN
      // console.log("TRIGGERED getMgepsToken ERROR: ")
      setAuthenticationProgress("Authentication Failed. Please try again.")
      setTimeout(() => {
        setAuthenticationProgress("Redirecting you to PhilGEPS login...")
        window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
      }, 1000) // 1 sec
    } else if (error && status === 403) {
      // REDIRECT TO PHILGEPS LOGIN
      // console.log("TRIGGERED getMgepsToken ERROR: ")
      setAuthenticationProgress(
        "Authentication Failed. 403 Token Unauthorized. Please try again."
      )
      setTimeout(() => {
        setAuthenticationProgress("Redirecting you to PhilGEPS login...")
        window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
      }, 1000) // 1 sec
    } else if (error && status === undefined) {
      if (counter === 0) {
        //Only show on first error
        setAuthenticationProgress("Authentication Failed. Server Error.")
      }
      if (token) {
        if (counter >= 1) {
          // Shows different message since it's re-authenticating
          // Prevent AutoLogin from running 3rd time and more if it still fails
          setAuthenticationProgress(
            "Re-authentication Failed. Please try again."
          )
          setTimeout(() => {
            setAuthenticationProgress("Redirecting you to PhilGEPS login...")
            window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
          }, 1000) // 1 sec
          return
        }
        // Re-execute AutoLogin if there's a token even if status code is undefined
        setTimeout(() => {
          setAuthenticationProgress("Trying to re-authenticate...")
        }, 1000) // 1 sec
        autoLogin()
      } else {
        // Just in case there's no token or some anomaly upon re-execution of AutoLogin for the 2nd time
        setTimeout(() => {
          setAuthenticationProgress("Redirecting you to PhilGEPS login...")
          window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
        }, 1000) // 1 sec
        return
      }
    }
  }, [logs])

  const autoLogin = useCallback(() => {
    // Verify Token authenticity #3 ----------------------------------------
    // console.log("TRIGGERED AUTOLOGIN w/ token")
    dispatch(getMgepsTokenThunk())
    setCounter(prevVal => prevVal + 1)
  }, [])

  useEffect(() => {
    //AUTH
    // console.log("TRIGGERED USE EFFECT AUTO LOGIN")
    if (token) {
      if (!token) {
        // Mimic Authentication when user visits root route
        // console.log("TRIGGERED REDIRECTION due to NO TOKEN")
        setTimeout(() => {
          setAuthenticationProgress(
            "Login Failed. Redirecting to PhilGEPS login..."
          )
          window.location.href = `${process.env.REACT_APP_EMARKET_BASE_URL}/login`
        }, 2000) // 2 secs
        return
      }
      autoLogin()
    }
  }, [autoLogin, token])

  return (
    <React.Fragment>
      <StyledContainer className="page-content">
        <StyledHeaderRow>
          <LogoWrapper>
            <img src={philGEPSLogo} alt="logo" style={{ width: "100%" }} />
          </LogoWrapper>
          {"PS-PhilGEPS"}
        </StyledHeaderRow>
        <WelcomeTextGroup>
          <Title> {"Welcome Back"}</Title>
          <SubTitle>{"Logging you in..."}</SubTitle>
        </WelcomeTextGroup>
        <StyledRow>
          <StyledSpinner color="primary"></StyledSpinner>
          {authenticationProgress}
        </StyledRow>
      </StyledContainer>
    </React.Fragment>
  )
}

export default Authenticate

// CONTAINER
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; // center vertically
  align-items: center; // center horizontally
  padding-top: 245px;
`

// WELCOME TEXT STYLES
const WelcomeTextGroup = styled(Row)`
  text-align: center;
  margin-top: 40px;
`

const Title = styled.p`
  font-size: 40px;
  font-weight: 600;
  color: #575757;
  margin-bottom: 6px;
`
const SubTitle = styled.p`
  font-size: 25px;
  font-weight: 500;
  color: #575757;
  margin-bottom: 0;
`

// HEADER STYLES
const StyledHeaderRow = styled(Row)`
  height: 82px;
  align-items: center;
  font-size: 34px;
  font-weight: 500;
  color: #212121;
`

const LogoWrapper = styled.div`
  width: 148px;
  height: 82px;
  align-items: center;
  display: flex;
  padding-left: 0;
  padding-right: 4px;
`

// LOADING PROGRESS STYLES
const StyledRow = styled(Row)`
  align-items: center;
  justify-content: center;
  font-size: 1.5625rem; // 20px
  font-weight: 500;
  margin-top: 163.75px;
  color: #575757;
`

const StyledSpinner = styled(Spinner)`
  margin-right: 20px;
  width: 3.25rem; //52px
  height: 3.25rem; //52px
  border-width: 6px; //8.8px thick
  border-right-color: #d1d1d1;
`
